/**
 * Client mirror of functions/src/cmr/cmrFieldRegistry.js.
 * Keep catalogs aligned when adding CMR fields — no free-text field ids in cmrDataFlow UI.
 */

/** @typedef {{ id: string, label: string, section: string, storage: 'healthRecords'|'medications', path: string, description?: string }} CmrFieldDef */

/** @type {CmrFieldDef[]} */
export const CMR_FIELD_REGISTRY = [
  {
    id: "firstName",
    label: "First name",
    section: "demographics",
    storage: "healthRecords",
    path: "firstName",
  },
  {
    id: "lastName",
    label: "Last name",
    section: "demographics",
    storage: "healthRecords",
    path: "lastName",
  },
  {
    id: "dateOfBirth",
    label: "Date of birth",
    section: "demographics",
    storage: "healthRecords",
    path: "dateOfBirth",
  },
  {
    id: "sex",
    label: "Sex",
    section: "demographics",
    storage: "healthRecords",
    path: "sex",
  },
  {
    id: "phone",
    label: "Phone",
    section: "demographics",
    storage: "healthRecords",
    path: "phone",
  },
  {
    id: "email",
    label: "Email",
    section: "demographics",
    storage: "healthRecords",
    path: "email",
  },
  {
    id: "address",
    label: "Address",
    section: "demographics",
    storage: "healthRecords",
    path: "address",
  },
  {
    id: "emergencyContact",
    label: "Emergency contact",
    section: "demographics",
    storage: "healthRecords",
    path: "emergencyContact",
  },
  {
    id: "weight",
    label: "Weight",
    section: "demographics",
    storage: "healthRecords",
    path: "weight",
  },
  {
    id: "weightDate",
    label: "Weight date",
    section: "demographics",
    storage: "healthRecords",
    path: "weightDate",
  },
  {
    id: "allergies",
    label: "Allergies",
    section: "allergies",
    storage: "healthRecords",
    path: "allergies",
    description: "Array of { allergen, type, reaction, notes, … }",
  },
  {
    id: "pmh",
    label: "Past medical history (conditions)",
    section: "conditions",
    storage: "healthRecords",
    path: "pmh",
  },
  {
    id: "psh",
    label: "Past surgical history",
    section: "surgical",
    storage: "healthRecords",
    path: "psh",
  },
  {
    id: "other",
    label: "Other history (social / family)",
    section: "other",
    storage: "healthRecords",
    path: "other",
  },
  {
    id: "other.smokingStatus",
    label: "Smoking status",
    section: "other",
    storage: "healthRecords",
    path: "other.smokingStatus",
  },
  {
    id: "other.alcoholUse",
    label: "Alcohol use",
    section: "other",
    storage: "healthRecords",
    path: "other.alcoholUse",
  },
  {
    id: "other.drugUse",
    label: "Drug use",
    section: "other",
    storage: "healthRecords",
    path: "other.drugUse",
  },
  {
    id: "other.occupation",
    label: "Occupation",
    section: "other",
    storage: "healthRecords",
    path: "other.occupation",
  },
  {
    id: "other.familyHistory",
    label: "Family history",
    section: "other",
    storage: "healthRecords",
    path: "other.familyHistory",
  },
  {
    id: "medications",
    label: "Medications list",
    section: "medications",
    storage: "medications",
    path: "",
    description: "Entire users/{uid}/medications subcollection",
  },
  {
    id: "medications.name",
    label: "Medication name",
    section: "medications",
    storage: "medications",
    path: "name",
  },
  {
    id: "medications.dosage",
    label: "Medication dosage",
    section: "medications",
    storage: "medications",
    path: "dosage",
  },
  {
    id: "medications.frequency",
    label: "Medication frequency",
    section: "medications",
    storage: "medications",
    path: "frequency",
  },
  {
    id: "medications.prescriber",
    label: "Medication prescriber",
    section: "medications",
    storage: "medications",
    path: "prescriber",
  },
  {
    id: "medications.reason",
    label: "Medication reason / notes",
    section: "medications",
    storage: "medications",
    path: "reason",
  },
];

const CMR_FIELD_IDS = new Set(CMR_FIELD_REGISTRY.map((f) => f.id));

export const VALID_CMR_WRITE_MODES = [
  "auto",
  "confirmRequired-by-patient",
  "confirmRequired-by-clinician",
];

export const CMR_WRITE_MODE_LABELS = {
  auto: "Auto-write to CMR",
  "confirmRequired-by-patient": "Patient must confirm",
  "confirmRequired-by-clinician": "Clinician must confirm",
};

export function listCmrFields() {
  return CMR_FIELD_REGISTRY.slice();
}

export function getCmrField(id) {
  const key = typeof id === "string" ? id.trim() : "";
  return CMR_FIELD_REGISTRY.find((f) => f.id === key) || null;
}

export function isValidCmrFieldId(id) {
  return CMR_FIELD_IDS.has(typeof id === "string" ? id.trim() : "");
}

export function isValidCmrWriteMode(mode) {
  return VALID_CMR_WRITE_MODES.includes(typeof mode === "string" ? mode.trim() : "");
}

export function formatCmrFieldLabel(id) {
  return getCmrField(id)?.label || id || "Unknown field";
}

const SHAREABLE_SECTION_ID_SET = new Set([
  "demographics",
  "conditions",
  "surgical",
  "medications",
  "allergies",
  "other",
]);

/**
 * Topic ids required by a TCI's active cmrDataFlow (inbound ∪ outbound → registry section).
 * Mirrors functions/src/cmr/cmrConsentGrants.js#sectionsFromCmrDataFlow.
 */
export function sectionsFromCmrDataFlow(tciDoc) {
  const flow = getActiveCmrDataFlow(tciDoc || {});
  const seen = new Set();
  const out = [];
  const rows = [...(flow.inbound || []), ...(flow.outbound || [])];
  for (const row of rows) {
    const fieldId = typeof row?.field === "string" ? row.field.trim() : "";
    if (!fieldId) continue;
    const section = getCmrField(fieldId)?.section;
    if (!section || !SHAREABLE_SECTION_ID_SET.has(section) || seen.has(section)) {
      continue;
    }
    seen.add(section);
    out.push(section);
  }
  return out;
}

/**
 * Active (live) cmrDataFlow — never reads cmrDataFlowPending.
 * Mirrors functions/src/cmr/cmrFieldRegistry.js#getActiveCmrDataFlow.
 */
export function getActiveCmrDataFlow(tciDoc) {
  const raw = tciDoc?.cmrDataFlow;
  if (!raw || typeof raw !== "object") {
    return { inbound: [], outbound: [] };
  }
  const inbound = Array.isArray(raw.inbound)
    ? raw.inbound
        .map((row) => {
          const field = typeof row?.field === "string" ? row.field.trim() : "";
          if (!field || !isValidCmrFieldId(field)) return null;
          return {
            field,
            label:
              (typeof row?.label === "string" && row.label.trim()) ||
              formatCmrFieldLabel(field),
          };
        })
        .filter(Boolean)
    : [];
  const outbound = Array.isArray(raw.outbound)
    ? raw.outbound
        .map((row) => {
          const field = typeof row?.field === "string" ? row.field.trim() : "";
          const writeMode =
            typeof row?.writeMode === "string" ? row.writeMode.trim() : "";
          if (!field || !isValidCmrFieldId(field) || !isValidCmrWriteMode(writeMode)) {
            return null;
          }
          return { field, writeMode };
        })
        .filter(Boolean)
    : [];
  return { inbound, outbound };
}

/**
 * Project fetchPatientCMR-shaped data to configured inbound field ids.
 * Missing values omitted — never throws.
 */
export function projectCmrInboundFields(cmrData, inbound) {
  const out = {};
  if (!cmrData || !Array.isArray(inbound)) return out;

  for (const row of inbound) {
    const fieldId = typeof row?.field === "string" ? row.field.trim() : "";
    if (!fieldId || !isValidCmrFieldId(fieldId)) continue;
    const def = getCmrField(fieldId);
    if (!def) continue;

    let value;
    if (def.storage === "medications") {
      if (fieldId === "medications") {
        value = cmrData.medications;
      } else {
        value = undefined;
      }
    } else if (def.section === "demographics") {
      value = cmrData.demographics?.[def.path];
      if (value === undefined || value === null || value === "") {
        value = cmrData[def.path];
      }
    } else if (fieldId.startsWith("other.")) {
      const key = def.path.replace(/^other\./, "");
      value = cmrData.other?.[key];
    } else if (fieldId === "other") {
      value = cmrData.other;
    } else if (fieldId === "allergies") {
      value = cmrData.allergies;
    } else if (fieldId === "pmh") {
      value = cmrData.pmh;
    } else if (fieldId === "psh") {
      value = cmrData.psh;
    } else {
      value = undefined;
    }

    if (value === undefined || value === null || value === "") continue;
    if (Array.isArray(value) && value.length === 0) continue;
    if (
      typeof value === "object" &&
      !Array.isArray(value) &&
      Object.keys(value).length === 0
    ) {
      continue;
    }
    out[fieldId] = value;
  }
  return out;
}
